import { Navigate, Route, Routes } from "react-router";
import { useAuth } from "react-oidc-context";

import { AppShell } from "@/components/shell/AppShell";
import { ErrorBox, Spinner } from "@/components/Spinner";
import { AudienceClassificationPage } from "@/pages/AudienceClassification";
import { AuthCallback } from "@/pages/AuthCallback";
import { BacklogPage } from "@/pages/Backlog";
import { BacklogItemEditPage } from "@/pages/BacklogItemEdit";
import { BuildsPage } from "@/pages/Builds";
import { CatalogComponentEditPage } from "@/pages/CatalogComponentEdit";
import { CatalogComponentsPage } from "@/pages/CatalogComponents";
import { CatalogComponentStatusPage } from "@/pages/CatalogComponentStatus";
import { CatalogGatewayEditPage } from "@/pages/CatalogGatewayEdit";
import { CatalogGatewaysPage } from "@/pages/CatalogGateways";
import { CatalogTypeEditPage } from "@/pages/CatalogTypeEdit";
import { DashboardPage } from "@/pages/Dashboard";
import { FactoryCredentialsPage } from "@/pages/FactoryCredentials";
import { FactoryRunnersPage } from "@/pages/FactoryRunners";
import { IntegrationsPage } from "@/pages/Integrations";
import { LoginScreen } from "@/pages/Login";
import { MeasurementPresetEditPage, MeasurementPresetsPage } from "@/pages/MeasurementPresets";
import { MessagingCampaignEditPage } from "@/pages/MessagingCampaignEdit";
import { MessagingCampaignsPage } from "@/pages/MessagingCampaigns";
import { MessagingEmailDetailPage } from "@/pages/MessagingEmailDetail";
import { MessagingEmailsPage } from "@/pages/MessagingEmails";
import { MessagingTemplateEditPage } from "@/pages/MessagingTemplateEdit";
import { MessagingTemplatesPage } from "@/pages/MessagingTemplates";
import { MetadataRecordingsPage } from "@/pages/MetadataRecordings";
import { OnboardingWizardPage } from "@/pages/OnboardingWizard";
import { SecurityPage } from "@/pages/Security";
import { SignedOut } from "@/pages/SignedOut";
import { StageFormPage } from "@/pages/StageForm";
import { TenantDeploymentsPage } from "@/pages/TenantDeployments";
import { TenantDetailPage } from "@/pages/TenantDetail";
import { TenantEditPage } from "@/pages/TenantEdit";
import { TenantIdentityProvidersPage } from "@/pages/TenantIdentityProviders";
import { TenantsPage } from "@/pages/Tenants";

export function App() {
  const auth = useAuth();

  // Plus de redirection automatique vers le Hosted UI : un visiteur non
  // connecté voit l'écran de connexion (Touch ID ou mot de passe). Les routes
  // /auth/callback et /auth/logout restent servies telles quelles pour que le
  // flux OIDC puisse se terminer.
  if (auth.error) return <ErrorBox error={auth.error} />;
  if (auth.isLoading) return <Spinner label="Authenticating…" />;

  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/logout" element={<SignedOut />} />
      {auth.isAuthenticated ? (
        <Route element={<AppShell />}>
          <Route index element={<DashboardPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/tenants" element={<TenantsPage />} />
          {/* A tenant is created here and only here: the wizard asks for the identifier, the
              two factors and the cost display before anything exists. There is no "/tenants/new":
              the address falls through to "/tenants/:tenantId" like any identifier. */}
          <Route path="/onboarding" element={<OnboardingWizardPage />} />
          <Route path="/tenants/:tenantId" element={<TenantDetailPage />} />
          <Route
            path="/tenants/:tenantId/deployments"
            element={<TenantDeploymentsPage />}
          />
          <Route path="/tenants/:tenantId/edit" element={<TenantEditPage />} />
          <Route path="/tenants/:tenantId/identity-providers" element={<TenantIdentityProvidersPage />} />
          <Route
            path="/tenants/:tenantId/stages/new"
            element={<StageFormPage />}
          />
          <Route
            path="/tenants/:tenantId/stages/:stage/edit"
            element={<StageFormPage />}
          />
          <Route path="/builds" element={<BuildsPage />} />
          <Route path="/backlog" element={<BacklogPage />} />
          <Route path="/backlog/new" element={<BacklogItemEditPage />} />
          {/* The Project item id is opaque and base64-ish, so it is encoded
              by the caller and decoded by useParams — never interpolated raw. */}
          <Route path="/backlog/:itemId/edit" element={<BacklogItemEditPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/audience-classification" element={<AudienceClassificationPage />} />
          <Route path="/measurement-presets" element={<MeasurementPresetsPage />} />
          <Route path="/measurement-presets/new" element={<MeasurementPresetEditPage />} />
          <Route path="/measurement-presets/:presetId" element={<MeasurementPresetEditPage />} />
          {/* Même patron que /metadata : la section pointe vers sa
              sous-section, et la redirection garde l'URL courte utilisable. */}
          <Route
            path="/factory"
            element={<Navigate to="/factory/runners" replace />}
          />
          <Route path="/factory/runners" element={<FactoryRunnersPage />} />
          <Route
            path="/factory/credentials"
            element={<FactoryCredentialsPage />}
          />
          <Route
            path="/metadata"
            element={<Navigate to="/metadata/recordings" replace />}
          />
          <Route path="/metadata/recordings" element={<MetadataRecordingsPage />} />
          <Route path="/messaging/emails" element={<MessagingEmailsPage />} />
          <Route path="/messaging/emails/:emailId" element={<MessagingEmailDetailPage />} />
          <Route path="/messaging/templates" element={<MessagingTemplatesPage />} />
          <Route path="/messaging/templates/new" element={<MessagingTemplateEditPage />} />
          <Route path="/messaging/templates/:slug/edit" element={<MessagingTemplateEditPage />} />
          <Route path="/messaging/campaigns" element={<MessagingCampaignsPage />} />
          <Route path="/messaging/campaigns/new" element={<MessagingCampaignEditPage />} />
          <Route path="/messaging/campaigns/:campaignId" element={<MessagingCampaignEditPage />} />
          <Route path="/catalog/gateways" element={<CatalogGatewaysPage />} />
          <Route
            path="/catalog/gateways/new"
            element={<CatalogGatewayEditPage />}
          />
          <Route
            path="/catalog/gateways/instances/:kind/:model/:version/edit"
            element={<CatalogGatewayEditPage />}
          />
          <Route
            path="/catalog/gateways/sections/:name/edit"
            element={<CatalogGatewayEditPage />}
          />
          <Route path="/catalog/gateways/types/new" element={<CatalogTypeEditPage />} />
          <Route
            path="/catalog/gateways/types/:model/:version/edit"
            element={<CatalogTypeEditPage />}
          />
          <Route path="/catalog/components" element={<CatalogComponentsPage />} />
          <Route
            path="/catalog/components/new"
            element={<CatalogComponentEditPage />}
          />
          <Route
            path="/catalog/components/:group/:name/edit"
            element={<CatalogComponentEditPage />}
          />
          <Route
            path="/catalog/components/:group/:name/status"
            element={<CatalogComponentStatusPage />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      ) : (
        <Route path="*" element={<LoginScreen />} />
      )}
    </Routes>
  );
}
